import React, { useCallback, useEffect, useRef, useState } from "react"
import { isEmpty } from "lodash"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import api from "utils/church_center_api"
import log from "utils/log"
import LaddaButton, { stopLaddaButton } from "components/ladda_button"
import { Loading } from "components/church_center/loading"
import { ErrorMessages, Field, TextInput } from "components/church_center/ui"
import RecaptchaForm from "components/recaptcha_form"
import {
  AddressField,
  CheckboxesField,
  DateField,
  DropdownField,
  FileInputField,
  GenderField,
  HeadingField,
  HouseholdField,
  NumberField,
  PhoneNumberField,
  TextAreaField,
  TextInputField,
  WorkflowCheckboxField,
  WorkflowCheckboxesField,
  WorkflowDropdownField,
  YesNoField,
} from "components/church_center/field_components"
import {
  buildIncludedParam,
  extractAuthErrors,
  extractFieldErrors,
  fetchAddressSuggestionDebounced,
  isEmptyish,
  optionIsSelectedInCheckboxes,
  optionIsSelectedInDropdown,
  optionallyLimitFieldErrors,
  trackEmailSuggestionClick,
  triggerIsCheckboxes,
  trimmedValues,
  uploadFileAttachment,
  valueIsBlankDropdownOtherOption,
} from "components/church_center/forms/form_utils"
import Icon from "components/church_center/cc_external_icon"
import Header from "./forms/header"
import NameBanner from "./forms/name_banner"
import Thanks from "./forms/thanks"

const TOTAL_FILE_SIZE_LIMIT = 1024 * 1024 * 10 * 5

export default function RecaptchaWrappedForm(props) {
  return (
    <RecaptchaForm {...props}>
      <Form />
    </RecaptchaForm>
  )
}

const Form = props => {
  const {
    defaultCountryCode,
    defaultFirstName,
    defaultLastName,
    defaultEmailAddress,
    id,
    name,
    description,
    sendSubmissionNotificationToSubmitter,
    submittedByEmailAddress,
    wrapSubmit,
    encryptedParam,
    recaptchaToken,
  } = props

  const [attachments, setAttachments] = useState([])
  const [countries, setCountries] = useState([])
  const [fields, setFields] = useState([])
  const [included, setIncluded] = useState([])
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [verificationRequired, setVerificationRequired] = useState(false)
  const [submissionConfirmation, setSubmissionConfirmation] = useState({})
  const [loggedInPerson, setLoggedInPerson] = useState(null)
  const [firstName, setFirstName] = useState(defaultFirstName ?? "")
  const [lastName, setLastName] = useState(defaultLastName ?? "")
  const [emailAddress, setEmailAddress] = useState(defaultEmailAddress ?? "")
  const [person, setPerson] = useState(props.person)
  const [suggestedEmailAddress, setSuggestedEmailAddress] = useState(null)

  const recaptchaRef = useRef(recaptchaToken)

  useEffect(() => {
    recaptchaRef.current = recaptchaToken
  }, [recaptchaToken])

  const headerRef = useRef()

  useEffect(() => {
    let allFields = []

    api.getAll(
      `/people/v2/forms/${id}/fields`,
      {
        order: "sequence",
        include:
          "options,field_definitions,field_options,marital_statuses,campuses,form_field_conditions,school_options,genders",
        per_page: "100",
        "fields[FieldOption]": "label,value2,sequence",
      },
      resp => {
        setFields(prevFields => {
          allFields = prevFields.concat(resp.data)
          return allFields
        })
        setIncluded(prevIncluded => {
          return prevIncluded.concat(resp.included)
        })
        setValues(prevValues => {
          return { ...prevValues, ...prepopulateValues(resp.data) }
        })
      },
      () => {
        const hasAddressField = allFields.some(
          field => field.attributes.field_type === "address"
        )

        if (hasAddressField) {
          api.get("/people/v2/countries", {}, countriesResp => {
            setCountries(countriesResp.data)
          })
        }

        if (person) {
          setLoading(false)
        } else {
          sessionApiClient
            .check()
            .then(browserSessionPerson => {
              if (
                browserSessionPerson.data.type === "VerifiedInSessionPerson"
              ) {
                setLoading(false)
                setLoggedInPerson(browserSessionPerson)
              } else {
                setLoading(false)
              }
            })
            .catch(() => setLoading(false))
        }
      }
    )
  }, [id, person, prepopulateValues])

  const fieldHasConditionNotMet = (field, fields) => {
    const condition = included.find(
      i =>
        i.type === "FormFieldCondition" &&
        i.relationships.form_field.data.id === field.id
    )

    if (!condition) return false

    const conditionTriggerId =
      condition.relationships.trigger_form_field.data.id
    const conditionOptionTrigger =
      condition?.relationships?.trigger_form_field_option?.data
    const conditionOptionValueTrigger =
      condition?.attributes?.trigger_form_field_option_value
    const conditionSelected = !isEmptyish(values[conditionTriggerId])

    const conditionTrigger = fields.find(f => f.id === conditionTriggerId)

    if (conditionOptionTrigger || conditionOptionValueTrigger) {
      // this trigger relies on an option of a form field
      if (
        conditionOptionValueTrigger &&
        !conditionOptionTrigger &&
        conditionTrigger
      ) {
        return (
          !conditionSelected ||
          values[conditionTriggerId] !== conditionOptionValueTrigger
        )
      }

      // check for whether this is the "other" option for a dropdown,
      // which is not allowed as an option trigger
      if (
        values[conditionTriggerId] &&
        !values[conditionTriggerId]?.selected &&
        valueIsBlankDropdownOtherOption(values[conditionTriggerId])
      ) {
        return true
      }

      const conditionOptionSelected = triggerIsCheckboxes(
        values,
        conditionTriggerId
      )
        ? optionIsSelectedInCheckboxes({
            conditionOptionTrigger,
            conditionTrigger,
            conditionTriggerId,
            included,
            values,
          })
        : optionIsSelectedInDropdown(
            values[conditionTriggerId],
            conditionOptionTrigger,
            conditionOptionValueTrigger
          )

      return !(conditionSelected && conditionOptionSelected)
    } else {
      // this trigger just relies on the form field itself
      return !conditionSelected
    }
  }

  const attachmentsForField = id => {
    const attachements = attachments.find(att => att.form_field_id === id)
    return attachements ? attachements["files"] : []
  }

  const genderOptions = (genders = []) => {
    return genders.data.map(o => {
      const {
        id,
        attributes: { value },
      } = findOption(o)
      return (
        <option key={id} value={id}>
          {value}
        </option>
      )
    })
  }

  const componentForType = field => {
    const { attributes, relationships, id } = field
    const { field_type, settings } = attributes
    const { phoneTypes, addressTypes, grades } = props

    const relevantErrors = errors.fieldErrors?.[id]

    const hide = fieldHasConditionNotMet(field, fields)

    if (hide) return null

    switch (field_type) {
      case "string":
        return (
          <TextInputField
            id={id}
            key={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            value={values[id]}
          />
        )
      case "checkboxes":
        return (
          <CheckboxesField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={relationships.options.data.map(o => findOption(o))}
            otherOption={settings && settings.other}
            value={values[id]}
          />
        )
      case "custom_checkboxes":
        return (
          <CheckboxesField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={relationships.field_options.data.map(o => findOption(o))}
          />
        )
      case "phone_number":
        return (
          <PhoneNumberField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleMergedValueChange}
            phoneTypes={phoneTypes}
            value={values[id]}
          />
        )
      case "text":
      case "medical":
      case "note":
        return (
          <TextAreaField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            value={values[id]}
          />
        )
      case "dropdown": {
        const options = relationships.options.data
          .map(o => findOption(o))
          .sort((a, b) => a.attributes.sequence - b.attributes.sequence)
        return (
          <DropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={options.map(({ attributes: { label }, id }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
            otherOption={settings && settings.other}
            value={values[id]}
          />
        )
      }
      case "custom_dropdown":
        return (
          <DropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={relationships.field_options.data.map(o => {
              const {
                attributes: { label },
                id,
              } = findOption(o)
              return (
                <option key={id} value={id}>
                  {label}
                </option>
              )
            })}
          />
        )
      case "address":
        return (
          <AddressField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            value={values[id]}
            countries={countries}
            locationOptions={addressTypes.map(at => (
              <option key={at}>{at}</option>
            ))}
            onChange={handleMergedValueChange}
          />
        )
      case "gender":
        return (
          <GenderField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={genderOptions(relationships.genders)}
            value={values[id]}
          />
        )
      case "workflow_checkbox":
        return (
          <WorkflowCheckboxField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={relationships.options.data.map(o => findOption(o))}
            value={values[id]}
          />
        )
      case "workflow_checkboxes":
        return (
          <WorkflowCheckboxesField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            onOtherChange={handleMergedValueChange}
            options={relationships.options.data.map(o => findOption(o))}
            otherOption={relationships.fieldable}
            otherOptionWithNoWorkflow={settings.no_workflow_for_other_option}
            value={values[id]}
          />
        )
      case "workflow_dropdown":
        return (
          <WorkflowDropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            onOtherChange={handleMergedValueChange}
            options={relationships.options.data.map(o => findOption(o))}
            otherOption={relationships.fieldable}
            otherOptionWithNoWorkflow={settings.no_workflow_for_other_option}
            value={values[id]}
          />
        )
      case "custom_field": {
        const fieldDefinition = findOption({
          id: relationships.field_definitions.data.id,
          type: "FieldDefinition",
        })

        const {
          attributes: { data_type, config },
        } = fieldDefinition

        let field_type

        switch (data_type) {
          case "select":
            field_type = "custom_dropdown"
            break
          case "checkboxes":
            field_type = "custom_checkboxes"
            break
          case "boolean":
            field_type = "custom_boolean"
            break
          default:
            field_type = data_type
            break
        }

        const component = componentForType({
          attributes: {
            ...attributes,
            settings: { ...settings, ...config },
            field_type,
          },
          relationships,
          id,
          hide,
        })

        return component
      }
      case "birthday":
        return (
          <DateField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            autoComplete="bday"
            onChange={handleValueChange}
            yearRange={"c-120Y:c"}
            className="date-field__input"
            value={values[id]}
          />
        )
      case "date":
      case "anniversary":
        return (
          <DateField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            yearRange={"-100:+20"}
            className="date-field__input"
          />
        )
      case "heading":
        return <HeadingField key={id} id={id} attributes={attributes} />
      case "boolean":
        return (
          <YesNoField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            value={values[id]}
          />
        )
      case "custom_boolean":
        return (
          <DropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={[
              <option key="yes" value={true}>
                Yes
              </option>,
              <option key="no" value={false}>
                No
              </option>,
            ]}
          />
        )
      case "file": {
        const fileUploadErrors = relevantErrors
          ? { attachments: relevantErrors.value }
          : {}
        const multipleFiles = !!attributes.settings["multiple_files"]

        return (
          <FileInputField
            key={id}
            id={id}
            attributes={attributes}
            errors={fileUploadErrors}
            props={props}
            onAddFile={handleAddAttachment}
            onRemoveFile={handleRemoveAttachment}
            multiFileInput={multipleFiles}
            maxTotalFileSize={TOTAL_FILE_SIZE_LIMIT}
            totalFileSizeExceedsLimit={totalFileSizeExceedsLimit()}
          />
        )
      }
      case "number":
        return (
          <NumberField
            key={id}
            id={id}
            value={values[id]}
            attributes={attributes}
            errors={relevantErrors}
            onChange={handleNumberValueChange}
          />
        )
      case "primary_campus":
        return (
          <DropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={relationships.campuses.data.map(o => {
              const {
                attributes: { name },
                id,
              } = findOption(o)
              return (
                <option key={id} value={id}>
                  {name}
                </option>
              )
            })}
          />
        )
      case "marital_status":
        return (
          <DropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={relationships.marital_statuses.data.map(o => {
              const {
                attributes: { label },
                id,
              } = findOption(o)
              return (
                <option key={id} value={id}>
                  {label}
                </option>
              )
            })}
          />
        )
      case "school":
        return (
          <DropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={renderSchoolGroupOptions(relationships.school_options)}
          />
        )
      case "grade":
        return (
          <DropdownField
            key={id}
            id={id}
            errors={relevantErrors}
            attributes={attributes}
            onChange={handleValueChange}
            options={grades.map(({ key, value }) => {
              return (
                <option key={key} value={key}>
                  {value}
                </option>
              )
            })}
          />
        )
      case "household":
        return (
          <HouseholdField
            key={id}
            id={id}
            values={values[id]}
            errors={relevantErrors}
            onChange={handleValueChange}
            attributes={attributes}
            phoneTypes={phoneTypes}
            genderOptions={genderOptions(relationships.genders)}
            maritalStatusOptions={relationships.marital_statuses.data.map(o => {
              const {
                attributes: { label },
                id,
              } = findOption(o)
              return (
                <option key={id} value={id}>
                  {label}
                </option>
              )
            })}
            grades={grades}
            schoolOptions={renderSchoolGroupOptions(
              relationships.school_options
            )}
          />
        )
      default:
        log.error("field_type isn't handled:", field_type)
        return null
    }
  }

  const renderSchoolGroupOptions = schoolOptions => {
    let schools = schoolOptions.data.map(schoolOption => {
      let school = findOption(schoolOption)

      return {
        id: schoolOption.id,
        label: school.attributes.label,
        value2: school.attributes.value2,
      }
    })

    return (
      <>
        {renderSchoolGroup(schools, "elementary", "Elementary schools")}
        {renderSchoolGroup(schools, "middle_school", "Middle Schools")}
        {renderSchoolGroup(schools, "high_school", "High Schools")}
        {renderSchoolGroup(schools, "college", "Colleges")}
        {renderSchoolGroup(schools, "other", "Other")}
      </>
    )
  }

  const findOption = ({ id, type }) => {
    return included.find(o => o.id === id && o.type === type)
  }

  const handleValueChange = useCallback((value, id) => {
    setValues(prevValues => {
      const newValues = { ...prevValues, [id]: value }

      if (isEmpty(newValues[id]) && newValues[id] !== true) {
        delete newValues[id]
      }

      return newValues
    })
  }, [])

  const handleNumberValueChange = (value, id) => {
    const parsedValue = parseFloat(value)
    const nextValue = isNaN(parsedValue) ? "" : value

    setValues(prevValues => ({
      ...prevValues,
      [id]: nextValue,
    }))
  }

  const handleMergedValueChange = useCallback((obj, id) => {
    if (obj === null) {
      setValues(prevValues => {
        const newValues = { ...prevValues }
        delete newValues[id]
        return newValues
      })
      return
    }
    setValues(prevValues => {
      const prev = prevValues[id] || {}
      const next = { ...prev, ...obj }

      const isEqual = Object.entries(obj).every(
        ([key, value]) => prev[key] === value
      )

      if (isEqual) {
        return prevValues
      }

      return { ...prevValues, [id]: next }
    })
  }, [])

  const handleAddressChange = e => {
    fetchAddressSuggestionDebounced(e.target.value, newState =>
      setSuggestedEmailAddress(newState)
    )

    setSuggestedEmailAddress(undefined)

    setEmailAddress(e.target.value)
  }

  const handleEmailSuggestionClick = () => {
    setEmailAddress(() => suggestedEmailAddress)
    setSuggestedEmailAddress(() => undefined)
    trackEmailSuggestionClick(emailAddress, suggestedEmailAddress)
  }

  const showFirstNameWarning = firstName => {
    return /(&|\sand\s|&amp;|\/|\\)/i.test(firstName)
  }

  const totalFileSizeExceedsLimit = () => {
    return (
      attachments
        .flatMap(attachment => attachment.files)
        .reduce((acc, currentFile) => acc + currentFile.size, 0) >
      TOTAL_FILE_SIZE_LIMIT
    )
  }

  const prepopulateValues = useCallback(
    fields => {
      const prepopulatedValues = {}
      fields.forEach(field => {
        if (field.attributes.field_type === "address") {
          prepopulatedValues[field.id] = {
            country_code: defaultCountryCode,
          }
        }
      })

      return prepopulatedValues
    },
    [defaultCountryCode]
  )

  const handleAddAttachment = attachment => {
    if (attachment) {
      const newAttachments = attachments.filter(
        att => att.form_field_id !== attachment.form_field_id
      )
      setAttachments([...newAttachments, attachment])
      handleValueChange("attachments", attachment.form_field_id)
    }
  }

  const handleRemoveAttachment = (formFieldId, fileName) => {
    const attachments =
      attachments?.filter(at => at.form_field_id !== formFieldId) || []

    const filesForFormField = attachmentsForField(formFieldId).filter(
      att => att.name !== fileName
    )

    const newAttachments =
      filesForFormField.length > 0
        ? [
            ...attachments,
            { form_field_id: formFieldId, files: filesForFormField },
          ]
        : attachments

    setAttachments(newAttachments)

    if (filesForFormField.length === 0) {
      handleValueChange("", formFieldId)
    }
  }

  const handleSubmit = async person => {
    const recaptchaToken = recaptchaRef.current
    const uploads = attachments.map(a => uploadFileAttachment(a))
    const uploadedAttachments = await Promise.all(uploads)

    const included = buildIncludedParam(
      trimmedValues(values),
      uploadedAttachments,
      fields
    )

    let attributes
    if (person.id) {
      attributes = {
        person_id: person.id,
        email_address: person.emailAddress,
        _recaptcha_token: recaptchaToken,
      }
      if (encryptedParam) {
        attributes["_e"] = encryptedParam
      }
    } else {
      attributes = {
        _recaptcha_token: recaptchaToken,
        email_address: person.emailAddress,
        person_attributes: {
          first_name: person.firstName,
          last_name: person.lastName,
          emails_attributes: [
            { location: "Home", address: person.emailAddress },
          ],
        },
      }
    }

    api.post(
      `/people/v2/forms/${id}/form_submissions`,
      {
        data: {
          type: "FormSubmission",
          attributes,
        },
        included,
      },
      resp => {
        if (resp.errors) {
          const formSubmissionErrors = resp.errors.find(
            e => e.source && e.source.parameter === "form_submission_values"
          )

          const errors = {
            authErrors: extractAuthErrors(resp.errors),
            fieldErrors: extractFieldErrors(formSubmissionErrors),
          }

          if (errors.fieldErrors) {
            optionallyLimitFieldErrors(fields, errors.fieldErrors)
          }

          if (
            !errors.fieldErrors &&
            (!errors.authErrors || isEmpty(errors.authErrors))
          ) {
            errors.formErrors = resp.errors[0].detail
          }

          if (!isEmpty(errors.authErrors)) {
            setErrors(errors)
            setPerson(null)
            setLoggedInPerson(null)
            stopLaddaButton()
          } else {
            setErrors(errors)
            stopLaddaButton()
          }

          if (headerRef.current) {
            setTimeout(() =>
              headerRef.current.scrollIntoView({ behavior: "smooth" })
            )
          }
        } else {
          setVerificationRequired(resp.meta.requires_verification)
          setSubmissionConfirmation({
            basicInformation: resp.meta.basic_information,
            groupedSubmissionValues: resp.meta.grouped_submission_values,
          })
          setSubmitted(true)
        }
      }
    )
  }

  const authErrors = errors.authErrors || {}
  const showTypoSuggestion =
    suggestedEmailAddress && emailAddress !== suggestedEmailAddress

  return loading ? (
    <div ref={headerRef}>
      <div className="mb-1">
        <Header name={name} description={description} />
      </div>
      <Loading />
    </div>
  ) : submitted ? (
    <Thanks
      attachments={attachments}
      verificationRequired={verificationRequired}
      sendSubmissionNotificationToSubmitter={
        sendSubmissionNotificationToSubmitter
      }
      submittedByEmailAddress={submittedByEmailAddress}
      submissionConfirmation={submissionConfirmation}
      onClick={() => {
        setValues(prepopulateValues(fields))
        setErrors([])
        setSubmissionConfirmation({})
        setVerificationRequired(false)
        setFirstName("")
        setLastName("")
        setEmailAddress(person ? person.data.attributes.email_address : "")
        setSubmitted(false)
        setLoading(false)
      }}
    />
  ) : (
    <div ref={headerRef}>
      <Header name={name} description={description}>
        {(!isEmpty(errors.fieldErrors) || !isEmpty(errors.authErrors)) && (
          <div className="my-2">
            <div className="quiet-alert error-alert alert d-f ai-c mb-2">
              <span className="icon c-ruby mr-1 fs-3">
                <Icon symbol="general#exclamation-triangle" aria-hidden />
              </span>
              <strong className="ta-l c-ruby">
                Uh oh! Looks like there were some problems.
              </strong>
            </div>
          </div>
        )}
      </Header>
      <form onSubmit={e => e.preventDefault()}>
        <section>
          <div>
            {loggedInPerson ? (
              <NameBanner person={loggedInPerson} info="My information" />
            ) : person ? (
              <NameBanner person={person} info="Submitting form for" />
            ) : (
              <div id="new-person-form">
                <Field id="1" field_type="your_name" label="Your name" required>
                  <div className="d-f@md f_1 d-b@iframe">
                    <div>
                      <TextInput
                        errors={authErrors["first name"]}
                        id="your_name_1"
                        placeholder="First name"
                        autoComplete="given-name"
                        autoFocus="autofocus"
                        value={firstName}
                        spellCheck={false}
                        onChange={e => setFirstName(e.target.value)}
                        required
                      />
                      <ErrorMessages
                        errors={authErrors["first name"]}
                        className="mt-1"
                      />
                      {showFirstNameWarning(firstName) && (
                        <div className="c-marigold mt-1">
                          For accuracy, use one name only.
                        </div>
                      )}
                    </div>
                    <div className="ml-0 ml-1@md ml-0@iframe mt-1 mt-0@md mt-1@iframe">
                      <label className="screen-reader-text" htmlFor="last_name">
                        Last name
                      </label>
                      <TextInput
                        id="last_name"
                        errors={authErrors["last name"]}
                        placeholder="Last name"
                        autoComplete="family-name"
                        value={lastName}
                        spellCheck={false}
                        onChange={e => setLastName(e.target.value)}
                        required
                      />
                      <ErrorMessages
                        errors={authErrors["last name"]}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </Field>
                <Field
                  id="1"
                  errors={authErrors["email address"]}
                  field_type="email_address"
                  label="Email address"
                  required
                >
                  <TextInput
                    errors={authErrors["email address"]}
                    type="email"
                    id="email_address_1"
                    placeholder="name@example.com"
                    autoComplete="email"
                    value={emailAddress}
                    onChange={handleAddressChange}
                    required
                    {...(showTypoSuggestion && {
                      style: {
                        boxShadow: "none",
                        outline: "none",
                        borderColor: "var(--color-citrine)",
                      },
                    })}
                  />
                  {showTypoSuggestion && (
                    <div
                      className="fs-4 mt-2p"
                      style={{
                        color: "var(--warning-alert--text)",
                      }}
                    >
                      Did you mean <strong>{suggestedEmailAddress}</strong>?{" "}
                      <button
                        className="btn sm-btn text-btn fs-5 h-2 td-u:h"
                        onClick={handleEmailSuggestionClick}
                      >
                        Replace
                      </button>
                    </div>
                  )}
                </Field>
              </div>
            )}

            {fields.map(f => componentForType(f))}

            <div className="my-4 ta-c">
              <LaddaButton
                onClick={() =>
                  wrapSubmit(handleSubmit)({
                    id: loggedInPerson
                      ? loggedInPerson.data.id
                      : person && person.data.id,
                    firstName,
                    lastName,
                    emailAddress: loggedInPerson
                      ? loggedInPerson.data.attributes.email_address
                      : emailAddress
                        ? emailAddress
                        : person.data.attributes.email_address,
                  })
                }
                spinnerColor="var(--color-tint3)"
                className="btn"
              >
                Submit
              </LaddaButton>
            </div>
          </div>
        </section>
        {errors.formErrors && (
          <p className="ta-c mt-1 c-ruby">{errors.formErrors}</p>
        )}
      </form>
    </div>
  )
}

const renderSchoolGroup = (schoolOptions, searchFor, label) => {
  const regex = new RegExp(searchFor)
  const schools = schoolOptions.filter(option => {
    return option.value2.match(regex)
  })

  return (
    <optgroup label={label}>
      {schools.map(function (school) {
        return (
          <option key={school.label} value={school.id}>
            {school.label}
          </option>
        )
      })}
    </optgroup>
  )
}
